import { useState, useEffect, useCallback, useRef } from 'react';

const BREAKPOINT = 768;

/**
 * Custom hook for toggling between card and table view modes.
 * Defaults to 'card' below 768px and 'table' at 768px+.
 * Once the user manually toggles, their choice sticks until unmount.
 *
 * @returns {Object} View toggle state and controls
 */
const useViewToggle = () => {
  const getDefaultMode = () => (window.innerWidth < BREAKPOINT ? 'card' : 'table');

  const [viewMode, setViewModeState] = useState(getDefaultMode);
  const userHasChosen = useRef(false);

  const setViewMode = useCallback((mode) => {
    userHasChosen.current = true;
    setViewModeState(mode);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (!userHasChosen.current) {
        setViewModeState(window.innerWidth < BREAKPOINT ? 'card' : 'table');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    viewMode,
    setViewMode,
    isCardView: viewMode === 'card',
    isTableView: viewMode === 'table',
  };
};

export default useViewToggle;
